import { useState, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change (handled by re-render)
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileCategoryOpen(false);
    setMobileGuidesOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-black/25 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">
          <a
            href="/"
            className={`font-heading text-lg md:text-xl font-bold tracking-tight whitespace-nowrap cursor-pointer transition-colors duration-300 ${
              scrolled ? 'text-foreground-950' : 'text-background-50'
            }`}
          >
            pandabuy.forum
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <a href="/" className={`text-sm font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer ${
              scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'
            }`}>
              Home
            </a>

            {/* Shopping Guide Dropdown */}
            <div className="relative group">
              <button className={`flex items-center gap-1 text-sm font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'
              }`}>
                Shopping Guide
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-3 max-h-80 overflow-y-auto shadow-sm">
                <div className="px-4 pb-2 mb-2 border-b border-background-200/50">
                  <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Shopping Guides</span>
                </div>
                {articles.map((article) => (
                  <a
                    key={article.slug}
                    href={`/article/${article.slug}`}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                  >
                    <span className="font-medium">{article.title}</span>
                    <span className="block text-xs text-foreground-400 mt-0.5 line-clamp-1">{article.excerpt}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Categories Dropdown */}
            <div className="relative group">
              <button className={`flex items-center gap-1 text-sm font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'
              }`}>
                Categories
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200/70 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-3 max-h-72 overflow-y-auto shadow-sm">
                <div className="px-4 pb-2 mb-2 border-b border-background-200/50">
                  <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Spreadsheet Categories</span>
                </div>
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-50 text-foreground-950 hover:bg-background-100'
              }`}
            >
              PandaBuy Sheet
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => {
              if (mobileMenuOpen) {
                closeMobileMenu();
              } else {
                setMobileMenuOpen(true);
              }
            }}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-300 ${
              scrolled ? 'text-foreground-950' : 'text-background-50'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 py-4 rounded-b-2xl shadow-sm max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="flex flex-col gap-1 px-2">
              <a href="/" onClick={closeMobileMenu} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-950 hover:bg-background-100 cursor-pointer">
                <i className="ri-home-4-line text-lg text-foreground-400"></i>
                Home
              </a>

              {/* Mobile Categories Accordion */}
              <button
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                className="flex items-center justify-between w-full px-3 py-3 rounded-lg text-sm font-semibold text-foreground-950 hover:bg-background-100 cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg text-foreground-400"></i>
                  Categories
                </span>
                <i className={`${mobileCategoryOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 transition-transform`}></i>
              </button>
              {mobileCategoryOpen && (
                <div className="grid grid-cols-2 gap-2 px-2 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMobileMenu}
                      className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}

              {/* Mobile Shopping Guide Accordion */}
              <button
                onClick={() => setMobileGuidesOpen(!mobileGuidesOpen)}
                className="flex items-center justify-between w-full px-3 py-3 rounded-lg text-sm font-semibold text-foreground-950 hover:bg-background-100 cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-book-open-line text-lg text-foreground-400"></i>
                  Shopping Guide
                </span>
                <i className={`${mobileGuidesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 transition-transform`}></i>
              </button>
              {mobileGuidesOpen && (
                <div className="px-2 pb-2 space-y-1">
                  {articles.map((article) => (
                    <a
                      key={article.slug}
                      href={`/article/${article.slug}`}
                      onClick={closeMobileMenu}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover" />
                      </div>
                      <span className="font-medium line-clamp-2">{article.title}</span>
                    </a>
                  ))}
                </div>
              )}

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="flex items-center justify-center gap-2 mx-2 mt-3 px-4 py-3 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                PandaBuy Sheet
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}